import React from 'react';
import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Box,
  Typography,
  Chip,
  SelectChangeEvent,
  InputAdornment
} from '@mui/material';
import { FormFieldProps } from '@/models/ui';
import './FormField.css';

const FormField: React.FC<FormFieldProps> = ({
  field,
  value,
  onChange,
  error,
  disabled = false,
  fullWidth = true,
  size = 'medium',
  variant = 'outlined',
  compact = false
}) => {
  const handleTextChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    let newValue = event.target.value;
    
    // Handle special field types
    if (field.name === 'item_code') {
      // Convert to uppercase for item_code
      newValue = newValue.toUpperCase();
    } else if (field.name === 'panno') {
      // Convert to uppercase for PAN number
      newValue = newValue.toUpperCase();
    } else if (field.name === 'gst_id') {
      // Convert to uppercase for GST ID
      newValue = newValue.toUpperCase();
    } else if (field.name === 'tel' || field.name === 'mobile' || field.name === 'fax') {
      // Handle phone number formatting
      newValue = newValue.replace(/\D/g, '').substring(0, 10);
    } else if (field.name === 'pin') {
      // Handle PIN code formatting
      newValue = newValue.replace(/\D/g, '').substring(0, 6);
    }
    
    onChange(newValue);
  };

  const handleSelectChange = (event: SelectChangeEvent<any>) => {
    onChange(event.target.value);
  };

  const handleNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseFloat(event.target.value) || 0;
    onChange(newValue);
  };

  // Format display value for phone numbers
  const formatPhoneDisplay = (value: any) => {
    if (!value) return '';
    if (field.name === 'tel' || field.name === 'mobile' || field.name === 'fax') {
      return value.toString();
    }
    return value;
  };

  // Get input props based on field type
  const getInputProps = () => {
    const baseProps: any = {};
    
    if (field.name === 'tel' || field.name === 'mobile' || field.name === 'fax') {
      baseProps.startAdornment = <InputAdornment position="start">+91</InputAdornment>;
      baseProps.maxLength = 10;
    } else if (field.name === 'rate') {
      baseProps.startAdornment = <InputAdornment position="start">₹</InputAdornment>;
    } else if (field.name === 'gst_percentage') {
      baseProps.endAdornment = <InputAdornment position="end">%</InputAdornment>;
    }
    
    return { ...baseProps, ...field.inputProps };
  };

  // Render label
  const renderLabel = () => (
    <Typography 
      variant="body2" 
      className="field-label"
      sx={{ 
        fontWeight: 600, 
        color: error ? '#ef4444' : '#374151',
        marginBottom: 1,
        fontSize: compact ? '0.8125rem' : '0.875rem'
      }}
    >
      {field.required && <span className="required-indicator">*</span>}
      {field.label}
    </Typography>
  );

  const renderField = () => {
    switch (field.type) {
      case 'select':
        
        
        return (
          <FormControl
            fullWidth={fullWidth}
            size={size}
            variant={variant}
            error={!!error}
            disabled={disabled}
            className={`form-field-select ${compact ? 'form-field-compact' : ''}`}
          >
            <Select
              value={value || ''}
              onChange={handleSelectChange}
              displayEmpty
              renderValue={(selected) => {
                if (!selected) {
                  return <span style={{ color: '#9ca3af' }}>{field.placeholder || 'Select an option'}</span>;
                }
                const option = field.options?.find(opt => opt.value === selected);
                return option ? option.label : selected;
              }}
            >
              {field.options?.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {error && <FormHelperText>{error}</FormHelperText>}
          </FormControl>
        );

      case 'textarea':
        return (
          <TextField
            fullWidth={fullWidth}
            size={size}
            variant={variant}
            value={value || ''}
            onChange={handleTextChange}
            error={!!error}
            helperText={error || field.helperText}
            disabled={disabled}
            multiline
            rows={field.rows || 4}
            className={`form-field-textarea ${compact ? 'form-field-compact' : ''}`}
            placeholder={field.placeholder}
            InputProps={{
              style: { 
                fontSize: compact ? '0.8125rem' : '0.875rem',
                padding: compact ? '10px 14px' : '12px 16px'
              }
            }}
          />
        );

      case 'number':
        return (
          <TextField
            fullWidth={fullWidth}
            size={size}
            variant={variant}
            type="number"
            value={value || ''}
            onChange={handleNumberChange}
            error={!!error}
            helperText={error || field.helperText}
            disabled={disabled}
            inputProps={{
              min: field.validation?.min,
              max: field.validation?.max,
              step: field.name === 'rate' ? 0.01 : field.name === 'gst_percentage' ? 0.01 : 'any',
              ...field.inputProps
            }}
            InputProps={{
              ...getInputProps(),
              style: { 
                fontSize: compact ? '0.8125rem' : '0.875rem',
                padding: compact ? '10px 14px' : '12px 16px'
              }
            }}
            className={`form-field-number ${compact ? 'form-field-compact' : ''}`}
            placeholder={field.placeholder}
          />
        );

      case 'phone':
        return (
          <TextField
            fullWidth={fullWidth}
            size={size}
            variant={variant}
            value={formatPhoneDisplay(value)}
            onChange={handleTextChange}
            error={!!error}
            helperText={error || field.helperText}
            disabled={disabled}
            inputProps={{
              maxLength: 10,
              pattern: '[0-9]{10}',
              ...field.inputProps
            }}
            InputProps={{
              ...getInputProps(),
              style: { 
                fontSize: compact ? '0.8125rem' : '0.875rem',
                padding: compact ? '10px 14px' : '12px 16px'
              }
            }}
            className={`form-field-phone ${compact ? 'form-field-compact' : ''}`}
            placeholder="Enter 10 digit number"
          />
        );

      case 'currency':
        return (
          <TextField
            fullWidth={fullWidth}
            size={size}
            variant={variant}
            type="number"
            value={value || ''}
            onChange={handleNumberChange}
            error={!!error}
            helperText={error || field.helperText}
            disabled={disabled}
            inputProps={{
              min: 0,
              step: 0.01,
              ...field.inputProps
            }}
            InputProps={{
              ...getInputProps(),
              style: { 
                fontSize: compact ? '0.8125rem' : '0.875rem',
                padding: compact ? '10px 14px' : '12px 16px'
              }
            }}
            className={`form-field-currency ${compact ? 'form-field-compact' : ''}`}
            placeholder="0.00"
          />
        );

      case 'email':
        return (
          <TextField
            fullWidth={fullWidth}
            size={size}
            variant={variant}
            type="email"
            value={value || ''}
            onChange={handleTextChange}
            error={!!error}
            helperText={error || field.helperText}
            disabled={disabled}
            className={`form-field-email ${compact ? 'form-field-compact' : ''}`}
            placeholder={field.placeholder}
            InputProps={{
              style: { 
                fontSize: compact ? '0.8125rem' : '0.875rem',
                padding: compact ? '10px 14px' : '12px 16px'
              }
            }}
          />
        );

      case 'password':
        return (
          <TextField
            fullWidth={fullWidth}
            size={size}
            variant={variant}
            type="password"
            value={value || ''}
            onChange={handleTextChange}
            error={!!error}
            helperText={error || field.helperText}
            disabled={disabled}
            className={`form-field-password ${compact ? 'form-field-compact' : ''}`}
            placeholder={field.placeholder}
            InputProps={{
              style: { 
                fontSize: compact ? '0.8125rem' : '0.875rem',
                padding: compact ? '10px 14px' : '12px 16px'
              }
            }}
          />
        );

      case 'date':
        return (
          <TextField
            fullWidth={fullWidth}
            size={size}
            variant={variant}
            type="date"
            value={value || ''}
            onChange={handleTextChange}
            error={!!error}
            helperText={error || field.helperText}
            disabled={disabled}
            className={`form-field-date ${compact ? 'form-field-compact' : ''}`}
            InputProps={{
              style: { 
                fontSize: compact ? '0.8125rem' : '0.875rem',
                padding: compact ? '10px 14px' : '12px 16px'
              }
            }}
          />
        );

      case 'uppercase':
        return (
          <TextField
            fullWidth={fullWidth}
            size={size}
            variant={variant}
            value={value || ''}
            onChange={handleTextChange}
            error={!!error}
            helperText={error || field.helperText}
            disabled={disabled}
            className={`form-field-uppercase ${compact ? 'form-field-compact' : ''}`}
            placeholder={field.placeholder}
            InputProps={{
              style: { 
                textTransform: 'uppercase',
                fontSize: compact ? '0.8125rem' : '0.875rem',
                padding: compact ? '10px 14px' : '12px 16px',
                letterSpacing: '0.5px'
              },
              ...field.inputProps
            }}
          />
        );

      default:
        return (
          <TextField
            fullWidth={fullWidth}
            size={size}
            variant={variant}
            value={value || ''}
            onChange={handleTextChange}
            error={!!error}
            helperText={error || field.helperText}
            disabled={disabled}
            className={`form-field-text ${compact ? 'form-field-compact' : ''}`}
            placeholder={field.placeholder}
            InputProps={{
              style: { 
                fontSize: compact ? '0.8125rem' : '0.875rem',
                padding: compact ? '10px 14px' : '12px 16px'
              }
            }}
          />
        );
    }
  };

  return (
    <Box className={`form-field-container ${compact ? 'form-field-compact' : ''}`}>
      {renderLabel()}
      {renderField()}
    </Box>
  );
};

export default FormField; 